import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Applies each route's Next.js-era `export const metadata` object to the
// document head at runtime. The Vite/React Router build dropped Next's
// server-side metadata rendering, so every route shipped the same <head>
// (one title + description for the whole site) — Google treated the
// marketing pages as duplicates and skipped indexing them.
//
// scripts/generate-seo-heads.mjs bakes the same titles/descriptions into
// per-route static HTML for the raw-HTML fetch; this hook keeps the rendered
// DOM in sync and covers client-side navigations. The title-suffix rule here
// must stay identical to that script's.

export const SITE_URL = "https://www.aiinterviewagents.com";
const DEFAULT_TITLE = "AI Interview Agents";
const DEFAULT_DESCRIPTION =
  "AI Interview Agents — AI-powered candidate interviews";
const APP_ROUTE_PREFIXES = [
  "/dashboard",
  "/auth",
  "/interview",
  "/i",
  "/certificate",
  "/report-preview",
  "/shared-video",
  "/shared-clip",
  "/demo",
];

export type PageMetadata = {
  title?: string;
  description?: string;
  robots?: { index?: boolean; follow?: boolean };
  openGraph?: { title?: string; description?: string };
  // false: the title is used exactly as given, without " | AI Interview Agents".
  brandSuffix?: boolean;
};

function setMetaTag(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function usePageMeta(meta: PageMetadata | undefined) {
  const { pathname, search } = useLocation();
  const isAppRoute = APP_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/")
  );
  // A bare /tools or /tools/<slug> is the canonical, indexable page. Any query
  // string on it (tracking params, accidental params) is a URL variant with no
  // distinct content, and free-tools pages specifically must never let a
  // parameterised URL get indexed or publicly cached (see nginx.conf's mirror
  // of this rule for the raw-HTML fetch).
  const isToolsRoute = pathname === "/tools" || pathname.startsWith("/tools/");
  const hasQueryVariant = isToolsRoute && search.length > 1;

  useEffect(() => {
    const title = meta?.title
      ? meta.brandSuffix === false
        ? meta.title
        : `${meta.title} | ${DEFAULT_TITLE}`
      : DEFAULT_TITLE;
    const description = meta?.description ?? DEFAULT_DESCRIPTION;
    // Query strings (?ref=producthunt) and trailing slashes are duplicate-URL
    // noise for crawlers; canonical always points at the bare www path.
    const canonical =
      SITE_URL + (pathname === "/" ? "/" : pathname.replace(/\/+$/, ""));

    document.title = title;
    setMetaTag("name", "description", description);

    // App routes must carry noindex and no canonical so Google drops them from
    // the index; robots.txt no longer blocks them so the directive is visible.
    let link = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    if (isAppRoute) {
      link?.remove();
      setMetaTag("name", "robots", "noindex, nofollow");
    } else if (hasQueryVariant) {
      link?.remove();
      setMetaTag("name", "robots", "noindex, nofollow");
    } else {
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);

      if (meta?.robots?.index === false) {
        setMetaTag("name", "robots", "noindex, nofollow");
      } else {
        document.head.querySelector('meta[name="robots"]')?.remove();
      }
    }

    setMetaTag("property", "og:title", meta?.openGraph?.title ?? title);
    setMetaTag(
      "property",
      "og:description",
      meta?.openGraph?.description ?? description
    );
    if (!isAppRoute && !hasQueryVariant) {
      setMetaTag("property", "og:url", canonical);
    }
  }, [isAppRoute, hasQueryVariant, meta, pathname]);
}
